import { getLocalTimeZone } from '@internationalized/date';
import { ThemeDensity, ColorScheme } from '@rematter/ui-web';
import { atom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';
import { useMediaQuery } from 'usehooks-ts';

export type Preferences = {
  localization: {
    language: string;
    defaultCountry: string;
    timezone: string;
  };

  theme: {
    colorScheme: ColorScheme | undefined;
    density: ThemeDensity;
  };

  touchModeEnabled: boolean;
};

const $preferences = atomWithStorage<Preferences>('preferences', {
  localization: {
    language: navigator.language.split('-')?.[0] ?? 'en',
    defaultCountry: navigator.language.split('-')?.[1] ?? 'US',
    timezone: getLocalTimeZone(),
  },

  theme: {
    colorScheme: undefined,
    density: 'default',
  },

  touchModeEnabled: false,
});

export const $localization = atom(
  (get) => get($preferences).localization,
  (get, set, localization: Partial<Preferences['localization']>) => {
    const currentPreferences = get($preferences);

    set($preferences, {
      ...currentPreferences,
      localization: {
        ...currentPreferences.localization,
        ...localization,
      },
    });
  },
);

export const $theme = atom(
  (get) => get($preferences).theme,
  (get, set, theme: Partial<Preferences['theme']>) => {
    const currentPreferences = get($preferences);

    set($preferences, {
      ...currentPreferences,
      theme: {
        ...currentPreferences.theme,
        ...theme,
      },
    });
  },
);

export const useSystemColorScheme = () => {
  const systemIsDarkMode = useMediaQuery('(prefers-color-scheme: dark)');
  return systemIsDarkMode ? 'dark' : 'light';
};

export const $touchModeEnabled = atom(
  (get) => get($preferences).touchModeEnabled,
  (get, set, touchModeEnabled: Partial<Preferences['touchModeEnabled']>) => {
    const currentPreferences = get($preferences);

    set($preferences, {
      ...currentPreferences,
      touchModeEnabled,
    });
  },
);
